/**
 * Ticket Message List Component
 *
 * Displays a conversation thread of ticket messages.
 * Distinguishes between customer and support messages.
 */

import React from 'react';
import { User, Shield } from 'lucide-react';
import type { TicketMessage } from '../../types/support';
import { formatDate } from '../../utils/formatting';

export interface TicketMessageListProps {
  /** Array of messages to display */
  messages: TicketMessage[];

  /** Show internal notes (admin view only) */
  showInternal?: boolean;
}

export const TicketMessageList: React.FC<TicketMessageListProps> = ({
  messages,
  showInternal = false,
}) => {
  if (messages.length === 0) {
    return (
      <div className="text-center py-8 text-neutral-600">
        <p>No messages yet. Start the conversation below.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages.map((message) => {
        // Hide internal notes unless showInternal is true
        if (message.isInternal && !showInternal) {
          return null;
        }

        const isCustomer = message.authorRole === 'company';
        const isInternal = message.isInternal;

        return (
          <div
            key={message.id}
            className={`flex gap-3 ${isCustomer ? 'flex-row' : 'flex-row-reverse'}`}
          >
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isCustomer
                    ? 'bg-primary-100 text-primary-700'
                    : isInternal
                      ? 'bg-warning-100 text-warning-700'
                      : 'bg-success-100 text-success-700'
                }`}
              >
                {isCustomer ? (
                  <User className="w-5 h-5" />
                ) : (
                  <Shield className="w-5 h-5" />
                )}
              </div>
            </div>

            {/* Message content */}
            <div className={`flex-1 max-w-2xl ${isCustomer ? 'text-left' : 'text-right'}`}>
              {/* Author and timestamp */}
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-neutral-900">
                  {message.authorName}
                </span>
                {isInternal && (
                  <span className="text-xs px-2 py-0.5 bg-warning-100 text-warning-700 rounded-full">
                    Internal Note
                  </span>
                )}
                <span className="text-xs text-neutral-500">{formatDate(message.createdAt)}</span>
                {message.editedAt && (
                  <span className="text-xs text-neutral-400">(edited)</span>
                )}
              </div>

              {/* Message text */}
              <div
                className={`p-3 rounded-lg ${
                  isCustomer
                    ? 'bg-neutral-100 text-neutral-900'
                    : isInternal
                      ? 'bg-warning-50 text-warning-900 border border-warning-200'
                      : 'bg-primary-600 text-white'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.message}</p>
              </div>

              {/* Attachments */}
              {message.attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {message.attachments.map((attachment) => (
                    <a
                      key={attachment.id}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-1 text-xs bg-neutral-100 hover:bg-neutral-200 rounded-md transition-colors"
                    >
                      <span>{attachment.fileName}</span>
                      <span className="text-neutral-500">
                        ({(attachment.fileSize / 1024).toFixed(1)} KB)
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
